import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';

const AddOnManagement = () => {
  const [addOnServices, setAddOnServices] = useState([]);
  const [userAddOns, setUserAddOns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('services');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    category: 'capacity',
    pricing_type: 'recurring',
    base_price: 0,
    price_per_unit: null,
    billing_cycle: 'monthly',
    minimum_quantity: 1,
    maximum_quantity: null,
    features: [],
    limit_increase: {},
    available_tiers: [],
    is_active: true,
    is_featured: false,
    icon_url: '',
    setup_required: false,
    auto_provision: true
  });

  useEffect(() => {
    fetchData();
  }, [selectedTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAddOnServices(),
        selectedTab === 'purchases' && fetchUserAddOns(),
        selectedTab === 'analytics' && fetchAnalytics()
      ].filter(Boolean));
    } finally {
      setLoading(false);
    }
  };

  const fetchAddOnServices = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/add-on-services`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAddOnServices(data);
      }
    } catch (error) {
      console.error('Error fetching add-on services:', error);
    }
  };

  const fetchUserAddOns = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/user-add-ons`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserAddOns(data);
      }
    } catch (error) {
      console.error('Error fetching user add-ons:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/add-on-analytics`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const createAddOnService = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/add-on-services`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowCreateDialog(false);
        resetForm();
        await fetchAddOnServices();
        alert('Add-on service created successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to create add-on service: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creating add-on service:', error);
      alert('Error creating add-on service. Please try again.');
    }
  };

  const updateAddOnService = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/add-on-services/${selectedService.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowEditDialog(false);
        setSelectedService(null);
        resetForm();
        await fetchAddOnServices();
        alert('Add-on service updated successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to update add-on service: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error updating add-on service:', error);
      alert('Error updating add-on service. Please try again.');
    }
  };

  const openEditDialog = (service) => {
    setSelectedService(service);
    setFormData({
      name: service.name,
      display_name: service.display_name,
      description: service.description,
      category: service.category,
      pricing_type: service.pricing_type,
      base_price: service.base_price,
      price_per_unit: service.price_per_unit,
      billing_cycle: service.billing_cycle,
      minimum_quantity: service.minimum_quantity,
      maximum_quantity: service.maximum_quantity,
      features: service.features || [],
      limit_increase: service.limit_increase || {},
      available_tiers: service.available_tiers || [],
      is_active: service.is_active,
      is_featured: service.is_featured,
      icon_url: service.icon_url || '',
      setup_required: service.setup_required,
      auto_provision: service.auto_provision
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      category: 'capacity',
      pricing_type: 'recurring',
      base_price: 0,
      price_per_unit: null,
      billing_cycle: 'monthly',
      minimum_quantity: 1,
      maximum_quantity: null,
      features: [],
      limit_increase: {},
      available_tiers: [],
      is_active: true,
      is_featured: false,
      icon_url: '',
      setup_required: false,
      auto_provision: true
    });
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'capacity': '📈',
      'storage': '💾',
      'features': '⚡',
      'support': '🎧',
      'analytics': '🤖',
      'international': '🌍',
      'reporting': '📋',
      'integration': '🔌'
    };
    return icons[category] || '📦';
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-100 text-green-800',
      'expired': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (!user?.is_admin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="text-red-400 text-6xl mb-4">⛔</div>
          <h3 className="text-lg font-medium text-red-900 mb-2">Access Denied</h3>
          <p className="text-red-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Add-on Management</h1>
        <p className="text-gray-600">Manage add-on services, monitor purchases, and view analytics</p>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          {[
            { id: 'services', label: 'Services', icon: '🔧' },
            { id: 'purchases', label: 'Purchases', icon: '🛒' },
            { id: 'analytics', label: 'Analytics', icon: '📊' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                selectedTab === tab.id
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Services Tab */}
      {selectedTab === 'services' && !loading && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Add-on Services</h2>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button>Create New Service</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create Add-on Service</DialogTitle>
                  <DialogDescription>
                    Create a new add-on service for users to purchase
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Service Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full p-2 border rounded-md"
                        placeholder="additional-users"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Display Name</label>
                      <input
                        type="text"
                        value={formData.display_name}
                        onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                        className="w-full p-2 border rounded-md"
                        placeholder="Additional Users"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full p-2 border rounded-md"
                      rows="3"
                      placeholder="Add more team members to your account"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="capacity">Capacity</option>
                        <option value="features">Features</option>
                        <option value="support">Support</option>
                        <option value="storage">Storage</option>
                        <option value="analytics">Analytics</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Pricing Type</label>
                      <select
                        value={formData.pricing_type}
                        onChange={(e) => setFormData({...formData, pricing_type: e.target.value})}
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="one_time">One-time</option>
                        <option value="recurring">Recurring</option>
                        <option value="usage_based">Usage-based</option>
                        <option value="tiered">Tiered</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Base Price ($)</label>
                      <input
                        type="number"
                        value={formData.base_price}
                        onChange={(e) => setFormData({...formData, base_price: parseFloat(e.target.value)})}
                        className="w-full p-2 border rounded-md"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Min Quantity</label>
                      <input
                        type="number"
                        value={formData.minimum_quantity}
                        onChange={(e) => setFormData({...formData, minimum_quantity: parseInt(e.target.value)})}
                        className="w-full p-2 border rounded-md"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Max Quantity</label>
                      <input
                        type="number"
                        value={formData.maximum_quantity || ''}
                        onChange={(e) => setFormData({...formData, maximum_quantity: e.target.value ? parseInt(e.target.value) : null})}
                        className="w-full p-2 border rounded-md"
                        min="1"
                        placeholder="Unlimited"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                        className="mr-2"
                      />
                      Active
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_featured}
                        onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                        className="mr-2"
                      />
                      Featured
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.setup_required}
                        onChange={(e) => setFormData({...formData, setup_required: e.target.checked})}
                        className="mr-2"
                      />
                      Setup Required
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2 mt-6">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={createAddOnService}>
                    Create Service
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Edit Service Dialog */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit Add-on Service</DialogTitle>
                <DialogDescription>
                  Update the add-on service details
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 max-h-96 overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Service Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full p-2 border rounded-md"
                      placeholder="additional-users"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Display Name</label>
                    <input
                      type="text"
                      value={formData.display_name}
                      onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                      className="w-full p-2 border rounded-md"
                      placeholder="Additional Users"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full p-2 border rounded-md"
                    rows="3"
                    placeholder="Add more team members to your account"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="capacity">Capacity</option>
                      <option value="features">Features</option>
                      <option value="support">Support</option>
                      <option value="storage">Storage</option>
                      <option value="analytics">Analytics</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Pricing Type</label>
                    <select
                      value={formData.pricing_type}
                      onChange={(e) => setFormData({...formData, pricing_type: e.target.value})}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="one_time">One-time</option>
                      <option value="recurring">Recurring</option>
                      <option value="usage_based">Usage-based</option>
                      <option value="tiered">Tiered</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Base Price ($)</label>
                    <input
                      type="number"
                      value={formData.base_price}
                      onChange={(e) => setFormData({...formData, base_price: parseFloat(e.target.value)})}
                      className="w-full p-2 border rounded-md"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Min Quantity</label>
                    <input
                      type="number"
                      value={formData.minimum_quantity}
                      onChange={(e) => setFormData({...formData, minimum_quantity: parseInt(e.target.value)})}
                      className="w-full p-2 border rounded-md"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Quantity</label>
                    <input
                      type="number"
                      value={formData.maximum_quantity || ''}
                      onChange={(e) => setFormData({...formData, maximum_quantity: e.target.value ? parseInt(e.target.value) : null})}
                      className="w-full p-2 border rounded-md"
                      min="1"
                      placeholder="Unlimited"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="mr-2"
                    />
                    Active
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                      className="mr-2"
                    />
                    Featured
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.setup_required}
                      onChange={(e) => setFormData({...formData, setup_required: e.target.checked})}
                      className="mr-2"
                    />
                    Setup Required
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-2 mt-6">
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={updateAddOnService}>
                  Update Service
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Strategic Overview Section */}
          <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <span className="text-2xl">📊</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-900">Strategic Overview</h3>
                <p className="text-sm text-blue-700">Monitor and optimize your add-on service portfolio aligned with subscription tiers</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="text-2xl font-bold text-green-600">{addOnServices.length}</div>
                <div className="text-sm text-gray-600">Total Services</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="text-2xl font-bold text-blue-600">
                  {addOnServices.filter(s => s.is_active).length}
                </div>
                <div className="text-sm text-gray-600">Active Services</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="text-2xl font-bold text-orange-600">
                  {addOnServices.filter(s => s.is_featured).length}
                </div>
                <div className="text-sm text-gray-600">Featured Services</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="text-2xl font-bold text-purple-600">
                  {new Set(addOnServices.map(s => s.category)).size}
                </div>
                <div className="text-sm text-gray-600">Categories</div>
              </div>
            </div>

            {/* Subscription Tier Alignment */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="font-medium text-gray-900 mb-3">Subscription Tier Strategy</h4>
              <p className="text-xs text-gray-600 mb-4">
                Add-ons provide premium features to lower tiers and specialized services to all tiers
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-center">
                <div className="p-3 bg-gray-50 rounded">
                  <div className="text-sm font-medium text-gray-700">Starter ($0)</div>
                  <div className="text-lg font-bold text-green-600">
                    {addOnServices.filter(s => s.available_tiers?.includes('starter')).length}
                  </div>
                  <div className="text-xs text-gray-500">Available Add-ons</div>
                </div>
                <div className="p-3 bg-blue-50 rounded">
                  <div className="text-sm font-medium text-blue-700">Professional ($49)</div>
                  <div className="text-lg font-bold text-blue-600">
                    {addOnServices.filter(s => s.available_tiers?.includes('professional')).length}
                  </div>
                  <div className="text-xs text-gray-500">Available Add-ons</div>
                </div>
                <div className="p-3 bg-green-50 rounded">
                  <div className="text-sm font-medium text-green-700">Business ($149)</div>
                  <div className="text-lg font-bold text-green-600">
                    {addOnServices.filter(s => s.available_tiers?.includes('business')).length}
                  </div>
                  <div className="text-xs text-gray-500">Available Add-ons</div>
                </div>
                <div className="p-3 bg-purple-50 rounded">
                  <div className="text-sm font-medium text-purple-700">Enterprise ($399)</div>
                  <div className="text-lg font-bold text-purple-600">
                    {addOnServices.filter(s => s.available_tiers?.includes('enterprise')).length}
                  </div>
                  <div className="text-xs text-gray-500">Available Add-ons</div>
                </div>
              </div>
            </div>

            {/* Category Portfolio Breakdown */}
            <div className="mt-4 bg-white p-4 rounded-lg shadow-sm">
              <h4 className="font-medium text-gray-900 mb-3">Category Portfolio</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(
                  addOnServices.reduce((acc, service) => {
                    const category = service.category || 'uncategorized';
                    acc[category] = (acc[category] || 0) + 1;
                    return acc;
                  }, {})
                ).map(([category, count]) => (
                  <div key={category} className="text-center p-3 bg-gray-50 rounded">
                    <div className="text-2xl mb-1">{getCategoryIcon(category)}</div>
                    <div className="text-sm font-medium text-gray-700 capitalize">{category}</div>
                    <div className="text-lg font-bold text-indigo-600">{count}</div>
                    <div className="text-xs text-gray-500">Services</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addOnServices.map((service) => (
              <Card key={service.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{getCategoryIcon(service.category)}</span>
                      <div>
                        <CardTitle className="text-lg">{service.display_name}</CardTitle>
                        <Badge variant="outline" className="text-xs mt-1">
                          {service.pricing_type.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      {service.is_featured && (
                        <Badge className="bg-orange-100 text-orange-800 text-xs">Featured</Badge>
                      )}
                      <Badge className={service.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {service.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-3">
                  <CardDescription className="text-sm">
                    {service.description}
                  </CardDescription>
                  
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Price:</span>
                    <span className="font-semibold">{formatPrice(service.base_price)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Min/Max Qty:</span>
                    <span>{service.minimum_quantity} - {service.maximum_quantity || '∞'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Available Tiers:</span>
                    <span>{service.available_tiers?.length ? service.available_tiers.join(', ') : 'All'}</span>
                  </div>
                  
                  {/* NEW: Features Unlocked - Phase 3 Enhancement */}
                  {service.features && service.features.length > 0 && (
                    <div className="pt-2 border-t">
                      <div className="text-xs text-gray-600 mb-1">🔓 Features Unlocked:</div>
                      <div className="flex flex-wrap gap-1">
                        {service.features.slice(0, 3).map((feature, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-blue-50 text-blue-700">
                            {feature}
                          </Badge>
                        ))}
                        {service.features.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{service.features.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* NEW: Limit Increases - Phase 3 Enhancement */}
                  {service.limit_increase && Object.keys(service.limit_increase).length > 0 && (
                    <div className="pt-2 border-t">
                      <div className="text-xs text-gray-600 mb-1">📈 Limit Increases:</div>
                      <div className="text-xs space-y-1">
                        {Object.entries(service.limit_increase).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-gray-600">{key.replace('_', ' ')}:</span>
                            <span className="font-semibold text-green-600">+{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="pt-2 border-t">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => openEditDialog(service)}
                    >
                      Edit Service
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Purchases Tab */}
      {selectedTab === 'purchases' && !loading && (
        <div>
          <h2 className="text-xl font-semibold mb-6">User Purchases</h2>
          
          <div className="space-y-4">
            {userAddOns.map((userAddOn) => {
              const service = addOnServices.find(s => s.id === userAddOn.add_on_service_id);
              
              return (
                <Card key={userAddOn.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <span className="text-2xl">{getCategoryIcon(service?.category)}</span>
                        <div>
                          <h3 className="font-semibold">{service?.display_name || 'Unknown Service'}</h3>
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <span>User ID: {userAddOn.user_id.slice(0, 8)}...</span>
                            <span>•</span>
                            <span>Qty: {userAddOn.quantity}</span>
                            <span>•</span>
                            <span>Purchased: {formatDate(userAddOn.purchased_at)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="font-semibold">{formatPrice(userAddOn.total_price)}</div>
                        <Badge className={getStatusColor(userAddOn.status)}>
                          {userAddOn.status}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {selectedTab === 'analytics' && !loading && analytics && (
        <div>
          <h2 className="text-xl font-semibold mb-6">Add-on Analytics</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {formatPrice(analytics.total_revenue)}
                  </div>
                  <div className="text-sm text-gray-600">Total Revenue</div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">
                    {analytics.total_purchases}
                  </div>
                  <div className="text-sm text-gray-600">Total Purchases</div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600">
                    {formatPrice(analytics.average_order_value)}
                  </div>
                  <div className="text-sm text-gray-600">Avg Order Value</div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Revenue by Category */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Revenue by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics.revenue_by_category.map((category, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">{getCategoryIcon(category._id)}</span>
                      <span className="font-medium">{category._id.replace('_', ' ')}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{formatPrice(category.revenue)}</div>
                      <div className="text-sm text-gray-600">{category.purchases} purchases</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AddOnManagement;